import type { Academy } from '@court-sync/shared';
import {
  LayoutDashboard,
  Calendar,
  CalendarDays,
  ClipboardList,
  GraduationCap,
  Search,
  Award,
  Ellipsis,
  Clock,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { buildAcademyPath, getAcademyHostMode } from '@/lib/academyPaths';
import type { SidebarNavItem, UnifiedConsumerNavOptions } from './sidebar-nav-config';
import type { UnifiedManagementNavOptions } from './sidebar-nav-config';

/** Opens the mobile sidebar sheet (full nav). */
export interface MobileSidebarTabItem {
  action: 'open-sidebar';
  label: string;
  icon: LucideIcon;
  mobileLabel?: string;
}

export type MobileTabItem = SidebarNavItem | MobileSidebarTabItem;

export function isMobileSidebarTabItem(item: MobileTabItem): item is MobileSidebarTabItem {
  return 'action' in item && item.action === 'open-sidebar';
}

type AcademyRouteFields = Pick<Academy, 'slug' | 'subdomain' | 'subdomainEnabled' | 'subscriptionTier'>;

function asAcademyFields(target: string | AcademyRouteFields): AcademyRouteFields {
  return typeof target === 'string' ? { slug: target } : target;
}

/** Guardian/student mobile bottom tab bar (order matters). */
export function getGuardianMobileTabItems(target: string | AcademyRouteFields): MobileTabItem[] {
  const academy = asAcademyFields(target);
  const hostMode = getAcademyHostMode();
  const p = (subpath: string) => buildAcademyPath(academy, subpath, hostMode);
  return [
    { to: p('/dashboard'), label: 'Dashboard', icon: LayoutDashboard },
    {
      to: p('/calendar'),
      label: 'My Calendar',
      mobileLabel: 'Calendar',
      icon: Calendar,
      tourId: 'student-calendar',
    },
    {
      to: p('/lessons'),
      label: 'Browse Lessons',
      mobileLabel: 'Browse',
      icon: Search,
      tourId: 'student-browse-lessons',
    },
    {
      to: p('/credits'),
      label: 'Make-up Credits',
      mobileLabel: 'Credits',
      icon: Award,
      tourId: 'student-credits',
    },
  ];
}

/** Coach mobile bottom tab bar (order matters). */
export function getCoachMobileTabItems(target: string | AcademyRouteFields): MobileTabItem[] {
  const academy = asAcademyFields(target);
  const hostMode = getAcademyHostMode();
  const p = (subpath: string) => buildAcademyPath(academy, subpath, hostMode);
  return [
    { to: p('/coach/dashboard'), label: 'Dashboard', icon: LayoutDashboard },
    { to: p('/coach/schedule'), label: 'My Schedule', icon: Calendar },
    { to: p('/coach/roster'), label: "Today's Roster", icon: ClipboardList },
    { to: p('/coach/students'), label: 'Students', icon: GraduationCap },
  ];
}

/** Primary shortcuts for the owner/admin mobile bottom tab bar (order matters). */
export const academyManagementMobileTabItems: MobileTabItem[] = [
  { to: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/admin/calendar', label: 'Calendar', icon: CalendarDays, tourId: 'manage-lessons' },
  { to: '/admin/bookings', label: 'Bookings', icon: ClipboardList, tourId: 'manage-bookings' },
  { action: 'open-sidebar', label: 'More', mobileLabel: 'More...', icon: Ellipsis },
];

/** Unified mobile tabs when academy and/or club management share one dashboard shell. */
export function getUnifiedManagementMobileTabItems(
  opts: UnifiedManagementNavOptions
): MobileTabItem[] {
  const items: MobileTabItem[] = [
    { to: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  ];

  if (opts.hasAcademyManagement) {
    items.push({
      to: '/admin/calendar',
      label: 'Calendar',
      mobileLabel: 'Calendar',
      icon: CalendarDays,
      tourId: 'manage-lessons',
    });
    items.push({
      to: '/admin/bookings',
      label: 'Bookings',
      icon: ClipboardList,
      tourId: 'manage-bookings',
    });
  } else if (opts.hasClubModule) {
    items.push({
      to: '/admin/calendar',
      label: 'Calendar',
      mobileLabel: 'Calendar',
      icon: CalendarDays,
    });
    items.push({
      to: '/admin/court-hire',
      label: 'Court hire',
      mobileLabel: 'Hire',
      icon: Clock,
    });
  }

  items.push({ action: 'open-sidebar', label: 'More', mobileLabel: 'More...', icon: Ellipsis });
  return items;
}

/** @deprecated Use getUnifiedManagementMobileTabItems */
export const clubManagementMobileTabItems: MobileTabItem[] = [
  { to: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/admin/calendar', label: 'Calendar', icon: CalendarDays },
  { to: '/admin/court-hire', label: 'Court hire', mobileLabel: 'Hire', icon: Clock },
  { action: 'open-sidebar', label: 'More', mobileLabel: 'More...', icon: Ellipsis },
];

/** Consumer portal mobile tabs (lessons + club member surfaces). */
export function getUnifiedConsumerMobileTabItems(opts: UnifiedConsumerNavOptions): MobileTabItem[] {
  const items: MobileTabItem[] = [
    { to: opts.pathFor('/dashboard'), label: 'Dashboard', icon: LayoutDashboard },
  ];

  if (opts.hasLessonsAccess) {
    items.push({
      to: opts.pathFor('/calendar'),
      label: 'My Calendar',
      mobileLabel: 'Calendar',
      icon: Calendar,
      tourId: 'student-calendar',
    });
    items.push({
      to: opts.pathFor('/lessons'),
      label: 'Browse Lessons',
      mobileLabel: 'Browse',
      icon: Search,
      tourId: 'student-browse-lessons',
    });
  } else if (opts.hasClubMemberAccess) {
    if (opts.hasMemberships) {
      items.push({
        to: opts.pathFor('/membership'),
        label: 'Membership',
        mobileLabel: 'Member',
        icon: GraduationCap,
      });
    }
    if (opts.hasCourtHire) {
      items.push({
        to: opts.pathFor('/court-hire'),
        label: 'Book court',
        mobileLabel: 'Book',
        icon: Clock,
      });
    }
  }

  items.push({ action: 'open-sidebar', label: 'More', mobileLabel: 'More...', icon: Ellipsis });
  return items;
}
